import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { setTokenProvider } from './api';

const authority = import.meta.env['VITE_COGNITO_AUTHORITY'] as string | undefined;
const clientId = import.meta.env['VITE_COGNITO_CLIENT_ID'] as string | undefined;
const hostedUi = import.meta.env['VITE_COGNITO_HOSTED_UI'] as string | undefined;

/** True when the build has the Cognito settings it needs. */
export const authConfigured = Boolean(authority && clientId);

function createUserManager(): UserManager | null {
  if (!authConfigured) return null;
  const origin = window.location.origin;
  return new UserManager({
    authority: authority!,
    client_id: clientId!,
    redirect_uri: `${origin}/admin/callback`,
    post_logout_redirect_uri: `${origin}/admin`,
    response_type: 'code',
    scope: 'openid email profile',
    // Tokens live in localStorage so a refresh does not bounce the admin out.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
    // Renew from the refresh token rather than a hidden iframe, which Cognito
    // does not reliably support.
    automaticSilentRenew: true,
    revokeTokensOnSignout: true,
    monitorSession: false,
  });
}

const manager = createUserManager();

interface JwtPayload {
  sub?: string;
  email?: string;
  username?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string[];
  exp?: number;
}

/**
 * Reads claims for display and menu decisions only. The API independently
 * verifies every token, so this does no signature checking.
 */
function decodeJwt(token: string): JwtPayload | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

export interface AuthState {
  ready: boolean;
  user: User | null;
  email: string | null;
  groups: string[];
  isAdmin: boolean;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  completeSignIn: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(!manager);
  const [error, setError] = useState<string | null>(
    authConfigured ? null : 'Admin sign-in is not configured for this build.',
  );
  // Kept in a ref so the token provider always sees the newest user without
  // being re-registered on every render.
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  useEffect(() => {
    if (!manager) return;

    let cancelled = false;
    manager
      .getUser()
      .then((existing) => {
        if (cancelled) return;
        // Expired sessions are discarded; the refresh happens on demand.
        setUser(existing && !existing.expired ? existing : existing);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    const onLoaded = (next: User) => setUser(next);
    const onUnloaded = () => setUser(null);
    const onExpired = () => {
      // Try a refresh; drop the session if it fails.
      manager!.signinSilent().catch(() => manager!.removeUser().catch(() => undefined));
    };

    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    manager.events.addAccessTokenExpired(onExpired);
    manager.events.addSilentRenewError(() => setError('Your session could not be renewed.'));

    return () => {
      cancelled = true;
      manager!.events.removeUserLoaded(onLoaded);
      manager!.events.removeUserUnloaded(onUnloaded);
      manager!.events.removeAccessTokenExpired(onExpired);
    };
  }, []);

  // Hand the API client a way to fetch a valid token, refreshing if needed.
  useEffect(() => {
    setTokenProvider(async () => {
      if (!manager) return null;
      const current = userRef.current ?? (await manager.getUser());
      if (!current) return null;
      if (!current.expired) return current.access_token;
      try {
        const refreshed = await manager.signinSilent();
        return refreshed?.access_token ?? null;
      } catch {
        await manager.removeUser().catch(() => undefined);
        return null;
      }
    });
  }, []);

  const signIn = useCallback(async () => {
    if (!manager) {
      setError('Admin sign-in is not configured for this build.');
      return;
    }
    setError(null);
    await manager.signinRedirect();
  }, []);

  const completeSignIn = useCallback(async () => {
    if (!manager) throw new Error('Admin sign-in is not configured for this build.');
    const signedIn = await manager.signinCallback();
    setUser(signedIn ?? (await manager.getUser()));
  }, []);

  const signOut = useCallback(async () => {
    if (!manager) return;
    await manager.removeUser().catch(() => undefined);
    setUser(null);
    // Cognito's hosted logout clears its own session cookie, which a local
    // token removal alone would leave behind.
    if (hostedUi && clientId) {
      const target = new URL(`${hostedUi.replace(/\/$/, '')}/logout`);
      target.searchParams.set('client_id', clientId);
      target.searchParams.set('logout_uri', `${window.location.origin}/admin`);
      window.location.assign(target.toString());
    } else {
      window.location.assign('/admin');
    }
  }, []);

  const value = useMemo<AuthState>(() => {
    const claims = user?.access_token ? decodeJwt(user.access_token) : null;
    const groups = claims?.['cognito:groups'] ?? [];
    const email =
      (user?.profile?.email as string | undefined) ??
      claims?.email ??
      claims?.username ??
      claims?.['cognito:username'] ??
      null;

    return {
      ready,
      user,
      email,
      groups,
      isAdmin: groups.includes('admins'),
      error,
      signIn,
      signOut,
      completeSignIn,
    };
  }, [user, ready, error, signIn, signOut, completeSignIn]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
